import i18n from 'i18next';
import config from '@config';
import en from './locales/en.json';

const supportedLanguages = ['en', 'en-GB', 'pl', 'de', 'cy', 'fr', 'es'];

const detectLanguage = (): string => {
	const lang = navigator.language || 'en';
	const baseLang = lang.split('-')[0];

	if (supportedLanguages.includes(lang)) {
		return lang;
	}

	if (supportedLanguages.includes(baseLang)) {
		return baseLang;
	}

	return 'en'; // fallback
};

const loadRemoteTranslations = async (lng: string): Promise<any> => {
	if (lng === 'en') {
		return {};
	}

	try {
		const response = await fetch(`https://cdn.agent-ex.ai/locales/${lng}.json`);
		if (!response.ok) {
			throw new Error('Failed to load translation');
		}

		return await response.json();
	} catch (err) {
		console.warn(`[Agent-Ex] Failed to load translation for "${lng}", falling back to English.`);
		return {};
	}
};

export const initI18n = async () => {
	const lng = detectLanguage();
	const resources: any = {
		en: { translation: en },
	};

	if (lng !== 'en') {
		const remoteTranslations = await loadRemoteTranslations(lng);
		resources[lng] = { translation: remoteTranslations };
	}

	await i18n.init({
		lng,
		fallbackLng: 'en',
		supportedLngs: supportedLanguages,
		ns: ['translation'],
		defaultNS: 'translation',
		resources,
		interpolation: {
			escapeValue: false,
		},
		debug: config.debug || false,
	});
};

export const setLanguage = async (lang: string) => {
	if (i18n.language === lang) {
		return;
	};

	// Load and add the resource bundle if it's not already loaded
	if (!i18n.hasResourceBundle(lang, 'translation')) {
		const remoteTranslations = await loadRemoteTranslations(lang);
		i18n.addResourceBundle(lang, 'translation', remoteTranslations, true, true);
	}

	await i18n.changeLanguage(lang);
};

export default i18n;
